import TableSkeletonComponent from "@/app/components/ui/LoaderComponent";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import Breed from "@/app/components/ui/Reusable/Breed";
import PetName from "@/app/components/ui/Reusable/PetName";
import Species from "@/app/components/ui/Reusable/Species";
import { useAppoitmentStatusList } from "@/app/hooks/api_hooks/dayClosing/useAppoitmentStatusList";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import { useLoader } from "@/app/hooks/useLoader";
import { useEffect, useState } from "react";
import { LuBox } from "react-icons/lu";
import { MdOutlineFileUpload } from "react-icons/md";
import UploadLabReportModal from "../../LabReportManagement/UploadLabReportModal";
import SamplePickupRequestModal from "../../LabReportManagement/SamplePickupRequestModal";
import EscalateReportModal from "../../LabReportManagement/EscalateReportModal";
import Username from "@/app/components/ui/Reusable/Username";
import { useLabReportFilterListAPI } from "@/app/hooks/api_hooks/LabReport/useLabReportFilterListAPI";
import LabReportTableComponent from "../../LabReportManagement/LabReportTableComponent";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLabReportDataList } from "@/app_redux/reducers/slices/auth/lab_report_slice";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import { ILabReportListResponseData } from "@/api_framework/api_modals/LabReport";

// Lab report filter API `type`: 3 = internal, 2 = incoming.
const LAB_TYPE_INTERNAL = 3;
const LAB_TYPE_INCOMING = 2;

// One list for the tab: internal rows first, then incoming. A test can come back
// in both responses (e.g. a requisition sent to the clinic's own lab), so dedupe.
const mergeLabRows = (...lists: (ILabReportListResponseData[] | undefined)[]) => {
  const seen = new Set<string>();
  return lists.flatMap((list) => list ?? []).filter((row: any) => {
    const key = `${row?.requisition_id}:${row?.test_id ?? row?.test_name}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const LabReportClosureComponent = ({ reportType }: { reportType?: string }) => {
  const [requisitionId, setRequisitionId] = useState(null);
  const [uploadModalType, setUploadModalType] = useState(null);
  const [invoiceId, setInvoiceId] = useState(null);
  const [uploadReport, setUploadReport] = useState(false);
  const [open, setOpen] = useState(false);
  const [esCalateReport, setEsCalateReport] = useState(false);

  const dispatch = useAppDispatch();

  const { execute: fetchDayClosingData, closureData } = useAppoitmentStatusList();

  const queryParams = new URLSearchParams(location.search);
  const isLoading = useLoader("isClosingDataInProgress");
  const { NO_APPOINTMENTS } = useImageConstants();

  const { execute: labReportApi } = useLabReportFilterListAPI();

  // ✅ read query params
  const processId = queryParams.get("closing_process") ?? "";
  const queryCity = queryParams.get("city_id");
  const queryClinics = queryParams.get("clinic_ids");
  const queryShift = queryParams.get("shift") ?? "day";
  const time = queryParams.get("t") ?? "";

  useEffect(() => {
    if (reportType !== "external") return;
    if (queryClinics) {
      dispatch(setLabReportDataList({ data: [] }));
      fetchDayClosingData(queryClinics, queryShift, "operations", "external_lab_reports");
    }
  }, [dispatch, fetchDayClosingData, processId, queryCity, queryClinics, queryShift, reportType, time]);

  useEffect(() => {
    if (reportType !== "external") return;
    const labreport = Array.isArray(closureData) ? closureData : closureData ? [closureData] : [];
    const flattenedClosureList = labreport.flatMap((report: any) =>
      Array.isArray(report?.closure_list) ? report.closure_list : [],
    );

    dispatch(setLabReportDataList({ data: flattenedClosureList }));
  }, [closureData, dispatch, reportType]);


  const today = new Date();

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });

  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);

  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");

  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");

  const formattedStartDate = `${startYear}-${startMonth}-${startDay}`;
  const formattedEndDate = `${endYear}-${endMonth}-${endDay}`;


  // Internal lab pendency = internal + incoming requests. Both calls used to
  // dispatch into the same list separately, so the last response wiped the
  // other. Fetch both silently, merge, and dispatch once; drop the result if
  // the filters changed (or the tab closed) before it came back.
  useEffect(() => {
    if (reportType === "external") return;
    if (!queryClinics) return;
    let cancelled = false;
    const setTableLoading = (status: boolean) =>
      dispatch(setLoading({ key: "isCustomerInvoiceTableInProgress", status }));

    const basePayload = {
      start_date: formattedStartDate,
      end_date: formattedEndDate,
      clinic_ids: [queryClinics],
      status_ids: [1, 2, 3, 6],
    };

    setTableLoading(true);
    Promise.all([
      labReportApi({ ...basePayload, type: LAB_TYPE_INTERNAL } as any, { silent: true }),
      labReportApi({ ...basePayload, type: LAB_TYPE_INCOMING } as any, { silent: true }),
    ]).then(([internal, incoming]) => {
      if (cancelled) return;
      dispatch(setLabReportDataList({ data: mergeLabRows(internal?.data, incoming?.data) }));
      setTableLoading(false);
    });

    return () => {
      cancelled = true;
      setTableLoading(false);
    };
  }, [
    dispatch,
    formattedStartDate,
    formattedEndDate,
    labReportApi,
    processId,
    queryCity,
    queryClinics,
    queryShift,
    reportType,
    time,
  ]);

  const isExternal = reportType === "external"
  return (
    <div className="w-full">
      <LabReportTableComponent scrollMode="table" source={isExternal} />
    </div>
  );
};

export default LabReportClosureComponent;
